<script lang="ts">
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import { categoryClickNavigationFlag, communitySlug } from "$stores/community";
  import { localStorageStore } from "$stores/localStorage";
  import KBNavItem from "./KBNavItem.svelte";
  import Action from "$components/+blocks/Dropdown/fields/Action.svelte";
  import Dropdown from "$components/+blocks/Dropdown/Dropdown.svelte";
  import { canChangeSpaceGroup, canDeleteSpaceGroup } from "$stores/permissions";
  import { dndzone, TRIGGERS } from "svelte-dnd-action";
  import { flip } from "svelte/animate";
  import { getMediaQuery } from "app/utils/mediaQuery";
  import { accordion } from "app/actions/accordion";
  import IconButtonTrigger from "$components/+blocks/Dropdown/fields/IconButtonTrigger.svelte";
  import { page } from "$app/stores";
  import cx from "classnames";
  import { Dots, Pencil, Section, Trash, Right } from "$components/+blocks/OVIcon";
  import { type Folder, isNestedDocument, type WritableFolder } from "app/models/documents";
  import {
    type DocumentsStore,
    DragType,
    isDraggingAtTheRoot,
    isDragging,
  } from "app/stores/documents";
  import {
    handleSharedDraggingBehaviour,
    isDndPlaceholder,
  } from "$components/knowledge-base/KnowledgeBaseNavigation/dndUtils";
  import DndPlaceholder from "$components/knowledge-base/KnowledgeBaseNavigation/DndPlaceholder.svelte";
  import Search from "$components/+blocks/Dropdown/fields/Search.svelte";
  import Option from "$components/+blocks/Dropdown/fields/Option.svelte";
  import OptionGroup from "$components/+blocks/Dropdown/fields/OptionGroup.svelte";
  import Separator from "$components/+blocks/Separator/Separator.svelte";
  import Avatar from "$components/+blocks/Avatar/Avatar.svelte";
  import { isDarkTheme } from "app/stores/userPreferences";
  import { tooltip } from "app/actions/tooltip";
  import { goto } from "$app/navigation";
  import { categoryURL } from "app/utils/urls";
  import { slugify } from "app/utils/strings";
  import { getStores } from "app/stores";
  import { browser } from "$app/environment";

  export let folder: WritableFolder;
  export let parentSection: WritableFolder | undefined;

  export let docs: DocumentsStore;
  export let isEditModalOpen: boolean = false;
  export let isDeleteModalOpen: boolean = false;
  export let hasDraggedEnteredParent: boolean = false;

  const { userPreferences } = getStores();

  const { currentDragType, sectionsList } = docs;
  const ANIMATION_DURATION_MS = 110;
  const isDesktop = getMediaQuery("desktop");

  // we persist the expansion state of the folder in local storage
  let isExpanded = localStorageStore(`${$folder.title}-${$communitySlug}-expansion`, false);
  let isDropdownOpen: boolean = false;
  let navItem: HTMLDivElement;

  // we reset the hasDraggedEnteredParent whenever the isDragging value changes
  $: {
    hasDraggedEnteredParent = false;
    $isDragging;
  }

  async function sortDocs(e: CustomEvent) {
    docs.preventUpdates(); // prevent server updates from colliding with user

    handleSharedDraggingBehaviour(e);
    if (e.detail.info.trigger === TRIGGERS.DRAG_STARTED) {
      $currentDragType = DragType.NestedDocument;
    }

    // if the user drags an item outside of the category and it does not have any parent, we set the drag value at the root state to true
    if (e.detail.info.trigger === TRIGGERS.DRAGGED_LEFT && !parentSection) {
      $isDraggingAtTheRoot = true;
    }

    // user has moved folder update UI
    $folder.items = e.detail.items;
    // folder has been dropped into a zone, update the order in the backend
    if (e.detail.info.trigger === TRIGGERS.DROPPED_INTO_ZONE) {
      const destination = $folder.items.findIndex((space) => space.uuid === e.detail.info.id);
      $folder.items[destination].moveAfterDragAndDrop(destination, $folder);
    }

    //dropped outside of any zone, we don't need to update the order
  }

  function changeSection(event: any) {
    const newParent = event.detail as Folder;
    folder.move(newParent);
  }

  $: path = categoryURL({
    communitySlug: $communitySlug,
    categoryUuid: $folder.uuid,
    categorySlug: slugify($folder.title),
  });

  $: isCurrentLocation = $page.url.pathname === path;
</script>

<button
  class={"text-[#707070] hover:text-gray-700/90 dark:text-gray-400 dark:hover:text-gray-300 " +
    "group flex h-[32px] w-full items-center gap-2.5 rounded pl-2 font-normal transition-colors duration-75 dark:border-transparent"}
  class:pr-2={!parentSection}
  class:pr-1.5={parentSection}
  on:click={() => {
    $isExpanded = !$isExpanded;

    // if the flag is active when the user clicks on the category, we navigate to the category page
    if ($categoryClickNavigationFlag) {
      goto(
        categoryURL({
          communitySlug: $communitySlug,
          categoryUuid: $folder.uuid,
          categorySlug: slugify($folder.title),
        })
      );

      if (!$isDesktop) {
        userPreferences.toggleLeftPanelExpansion();
      }
    }
  }}
>
  <span
    class="flex grow items-center truncate py-0.5 text-left text-sm md:text-[13px]"
    use:tooltip
    title={$folder.title}
    class:text-community-primary={isCurrentLocation}
  >
    {#if $folder.icon}
      {@const icon = $isDarkTheme && $folder.iconDark ? $folder.iconDark : $folder.icon}
      <div class="mr-2">
        <Avatar mainImage={icon} radius="rounded-[3px]" />
      </div>
    {/if}

    <p class="truncate">{$folder.title}</p>
  </span>

  {#if $canChangeSpaceGroup || $canDeleteSpaceGroup}
    <div
      class="justify-center group-hover:flex"
      class:flex={isDropdownOpen}
      class:hidden={!isDropdownOpen}
    >
      <div class="mr-2 w-2.5">
        <Dropdown bind:open={isDropdownOpen}>
          <IconButtonTrigger
            size="small"
            slot="trigger"
            icon={Dots}
            a11yLabel={"More actions"}
            let:active
            {active}
          />

          {#if $canChangeSpaceGroup}
            <Action icon={Pencil} title="Edit category" on:click={() => (isEditModalOpen = true)} />
            <Dropdown>
              <Action
                slot="trigger"
                title="Move section"
                icon={Section}
                let:active
                {active}
                closeOnTrigger={false}
              />
              <Search placeholder="Set section..." />
              <Separator class="w-full" />
              <OptionGroup value={$parentSection} on:select={changeSection}>
                {#each $sectionsList as folder}
                  {@const title = folder.isDefault ? "Default section" : folder.title}
                  <Option {title} value={folder} key={(f) => f.uuid} />
                {/each}
              </OptionGroup>
            </Dropdown>
          {/if}

          {#if $canDeleteSpaceGroup}
            <Action
              icon={Trash}
              title="Delete category"
              danger
              on:click={() => (isDeleteModalOpen = true)}
            />
          {/if}
        </Dropdown>
      </div>
    </div>
  {/if}

  <div
    class:group-hover:hidden={$canChangeSpaceGroup || $canDeleteSpaceGroup}
    class:hidden={isDropdownOpen}
  >
    <OVIcon
      icon={Right}
      size="size-3.5"
      class={cx("transition-transform duration-75", $isExpanded ? "rotate-90" : "rotate-0")}
      aria-expanded={$isExpanded}
    />
  </div>
</button>

<!-- initially hide accordian content in SSR (via style) then allow accordian action to open -->
<nav
  class="ml-3 no-scrollbar min-h-0 rounded"
  style:height={browser ? "" : "0px"}
  use:dndzone={{
    items: $folder.items,
    flipDurationMs: ANIMATION_DURATION_MS,
    dragDisabled: !$canChangeSpaceGroup || !$isDesktop,
    type: "docs",
    dropTargetClasses: [
      "border",
      "border-dashed",
      "border-blue-400/75",
      "dark:border-blue-400/45",
      "transition-[min-height]",
      "duration-[400ms]",
      "ease-out",
      "mr-2",
      "-mt-[3px]",
      "mb-[1px]",
      "delay-[25ms]",
    ],
    dropTargetStyle: {
      // this is a hack to make the category preserved space expand or shrink when dragging a document over its parent section,
      // or when dragging a document over the root level
      minHeight:
        $currentDragType === "nested_document" &&
        (hasDraggedEnteredParent || ($isDraggingAtTheRoot && !parentSection))
          ? "16px"
          : "0",
    },
    // Only accepts documents
    dropFromOthersDisabled: $currentDragType !== "nested_document",
  }}
  on:consider={(e) => {
    // user has started dragging folder, this expands it
    $isExpanded = true;
    sortDocs(e);
  }}
  on:finalize={sortDocs}
  use:accordion={{ isOpen: $isExpanded, duration: ANIMATION_DURATION_MS }}
>
  {#if $folder.items.length === 0}
    <!--
    Important, when these nav dropzones are completely empty, there is a bug where dropzones don't open properly. It is
    not enough to set min height in dropTargetClasses.
    -->
    <div />
  {/if}
  {#each $folder.items as document (document.uuid)}
    <div animate:flip={{ duration: ANIMATION_DURATION_MS }}>
      {#if isDndPlaceholder(document)}
        <DndPlaceholder />
        <!-- This condition should always be true here in Categories, because they can only have Document children -->
        <!-- just checking it for typescript -->
      {:else if isNestedDocument(document)}
        <div
          class={cx(
            "border-l pl-2 transition-colors duration-100 ease-out",
            $page.url.pathname.includes(document.uuid)
              ? "border-community-primary"
              : "border-black/8 hover:border-black/25 hover:text-gray-700 dark:border-white/8 dark:hover:border-gray-400/60"
          )}
          class:pr-0.5={!parentSection}
          bind:this={navItem}
        >
          <KBNavItem
            communitySlug={$communitySlug}
            {document}
            on:expand-group={() => {
              if (!$isExpanded) $isExpanded = true;
              setTimeout(() => {
                navItem?.scrollIntoView({
                  block: "nearest",
                  behavior: "smooth",
                });
              }, ANIMATION_DURATION_MS * 2);
            }}
          />
        </div>
      {/if}
    </div>
  {/each}
</nav>
